<template>
  <div>
    <InventoryHeaderTab />

    <div class="page-header">
      <div>
        <h5 class="page-title">New Stock Requisition</h5>
        <p class="page-sub">Request stock items for internal use</p>
      </div>
      <button type="button" @click="$router.go(-1)" class="btn-outline">
        <font-awesome-icon icon="arrow-left" /> Back
      </button>
    </div>

    <div v-if="validationErrors.length" class="error-banner">
      <font-awesome-icon icon="exclamation-circle" class="error-icon" />
      <ul class="mb-0 ps-1">
        <li v-for="(err, i) in validationErrors" :key="i">{{ err }}</li>
      </ul>
    </div>

    <form @submit.prevent="submit">

      <!-- Header Details -->
      <div class="form-card mb-4">
        <div class="section-header">
          <div class="section-icon section-icon--blue">
            <font-awesome-icon icon="clipboard-list" />
          </div>
          <div>
            <p class="section-title">Requisition Details</p>
            <p class="section-sub">Specify the store, recipient and purpose of this issue</p>
          </div>
        </div>

        <div class="fields-grid fields-grid--2">
          <div class="field-group">
            <label class="field-label">Date <span class="req">*</span></label>
            <div class="datepicker-wrap">
              <font-awesome-icon icon="calendar" class="datepicker-icon" />
              <Datepicker v-model="date" :enableTimePicker="false" class="clean-datepicker" placeholder="Select date" />
            </div>
          </div>

          <div class="field-group">
            <label class="field-label">Store <span class="req">*</span></label>
            <Multiselect
              v-model="store_id"
              label="name"
              :options="getStore"
              placeholder="Choose a store"
              :searchable="true"
              :create-option="false"
              :close-on-select="true"
              @select="onStoreSelect(store_id)"
              class="custom-multiselect"
            />
          </div>

          <!-- Requisition For -->
          <div class="field-group">
            <label class="field-label">Requisition For <span class="req">*</span></label>
            <select class="field-input field-select" v-model="requisition_for" @change="onRequisitionTypeChange">
              <option value="">Select type</option>
              <option value="department">Department</option>
              <option value="individual">Individual</option>
            </select>
          </div>

          <div class="field-group">
            <label class="field-label">Purpose <span class="req">*</span></label>
            <input type="text" class="field-input" v-model="purpose" placeholder="Brief reason for this stock request" />
          </div>

          <!-- Department fields -->
          <template v-if="requisition_for === 'department'">
            <div class="field-group">
              <label class="field-label">Department <span class="req">*</span></label>
              <Multiselect
                v-model="department_id"
                label="name"
                :options="departments"
                placeholder="Select department"
                :searchable="true"
                :create-option="false"
                :close-on-select="true"
                @select="onDepartmentSelect"
                class="custom-multiselect"
              />
            </div>

            <div class="field-group">
              <label class="field-label">Unit</label>
              <Multiselect
                v-model="unit_id"
                label="name"
                :options="units"
                placeholder="Select unit"
                :searchable="true"
                :create-option="false"
                :close-on-select="true"
                :disabled="!department_id"
                class="custom-multiselect"
              />
            </div>
          </template>

          <!-- Individual field -->
          <div v-if="requisition_for === 'individual'" class="field-group field-group--full">
            <label class="field-label">Individual Details <span class="req">*</span></label>
            <input
              type="text"
              class="field-input"
              v-model="individual_details"
              placeholder="e.g. John Doe – Admin Staff"
            />
          </div>

          <div class="field-group field-group--full">
            <label class="field-label">Notes</label>
            <textarea class="field-input" v-model="notes" rows="2" placeholder="Any additional notes..."></textarea>
          </div>
        </div>
      </div>

      <!-- Products Table -->
      <div class="form-card mb-4">
        <div class="section-header">
          <div class="section-icon section-icon--teal">
            <font-awesome-icon icon="boxes" />
          </div>
          <div>
            <p class="section-title">Products Requested</p>
            <p class="section-sub">Add the products you need from the selected store</p>
          </div>
        </div>

        <div class="table-outer-scroll">
        <div class="table-scroll-wrap">
          <table class="req-table">
            <thead>
              <tr>
                <th style="min-width:220px">Product</th>
                <th style="min-width:140px">Variant / UOM</th>
                <th style="min-width:100px">Available</th>
                <th style="min-width:90px">Qty</th>
                <th style="min-width:160px">Notes</th>
                <th style="min-width:44px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, idx) in items" :key="idx">

                <td>
                  <Multiselect
                    v-model="item.product_id"
                    label="name"
                    :options="storeProducts"
                    placeholder="Select product"
                    :searchable="true"
                    :create-option="false"
                    :close-on-select="true"
                    @select="onProductSelect(item.product_id, idx)"
                    :disabled="!store_id"
                    class="custom-multiselect custom-multiselect--sm"
                  />
                </td>

                <td>
                  <select v-if="item.variants.length > 0" class="tbl-input tbl-select"
                    v-model="item.product_variant_id">
                    <option disabled value="">Select variant</option>
                    <option v-for="v in item.variants" :key="v.id" :value="v.id">{{ v.variant_name }}</option>
                  </select>
                  <input v-else type="text" class="tbl-input" :value="item.uom" disabled />
                </td>

                <td>
                  <input type="text" class="tbl-input tbl-input--center" :value="item.available_qty" disabled />
                </td>

                <td>
                  <input type="number" class="tbl-input tbl-input--center" v-model="item.quantity" min="1"
                    :disabled="!item.product_id"
                    onkeypress="return /^[0-9]+$/i.test(event.key)" />
                </td>

                <td>
                  <input type="text" class="tbl-input" v-model="item.notes" placeholder="Optional note" />
                </td>

                <td>
                  <button class="btn-remove-row" type="button" @click="removeItem(idx)" title="Remove">
                    <font-awesome-icon icon="times" />
                  </button>
                </td>

              </tr>
            </tbody>
          </table>
        </div>
        </div>

        <div class="table-footer-actions">
          <button type="button" class="btn-add-row" @click="addItem">
            <font-awesome-icon icon="plus" class="me-2" /> Add Product
          </button>
        </div>
      </div>

      <div class="form-footer">
        <button type="button" class="btn-outline" @click="$router.go(-1)">Cancel</button>
        <button type="submit" class="btn-primary-action" :disabled="crudLoading">
          <span v-if="crudLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
          <font-awesome-icon v-else icon="paper-plane" class="me-2" />
          {{ crudLoading ? 'Submitting...' : 'Submit Requisition' }}
        </button>
      </div>

    </form>
  </div>
</template>

<script>
import InventoryHeaderTab from "./component/inventoryheadertab.vue";
import Multiselect from '@vueform/multiselect';
import { ref } from "@vue/reactivity";
import { onMounted } from "@vue/runtime-core";
import axios from "axios";
import { useToast } from "vue-toastification";
import { useRouter } from "vue-router";
import { useReusable } from "../../../lib/reusable";
import { reusableMixin } from "../../../lib/reusableMixin";

const EMPTY_ITEM = () => ({
  product_id: '', product_variant_id: '', variants: [], uom: '', available_qty: 0, quantity: 1, notes: '',
});

export default {
  components: { InventoryHeaderTab, Multiselect },
  mixins: [reusableMixin],

  setup() {
    const toast            = useToast();
    const router           = useRouter();
    const crudLoading      = ref(false);
    const validationErrors = ref([]);

    const date               = ref(new Date());
    const store_id           = ref(null);
    const requisition_for    = ref('');
    const department_id      = ref(null);
    const unit_id            = ref(null);
    const individual_details = ref('');
    const purpose            = ref('');
    const notes              = ref('');
    const items              = ref([EMPTY_ITEM()]);
    const storeProducts      = ref([]);
    const departments        = ref([]);
    const units              = ref([]);

    const { getStore, allStore } = useReusable();

    const loadDepartments = async () => {
      try {
        const res = await axios.get('/payroll/department/active');
        const data = res?.data?.data ?? [];
        departments.value = data.map(d => ({ value: d.id, name: d.name }));
      } catch { departments.value = []; }
    };

    const loadUnits = async (departmentId = null) => {
      units.value = [];
      try {
        const params = departmentId ? { department_id: departmentId } : {};
        const res = await axios.get('/payroll/unit/active', { params });
        const data = res?.data?.data ?? [];
        units.value = data.map(u => ({ value: u.id, name: u.name }));
      } catch { units.value = []; }
    };

    const onRequisitionTypeChange = () => {
      department_id.value      = null;
      unit_id.value            = null;
      individual_details.value = '';
      units.value              = [];
    };

    const onDepartmentSelect = (val) => {
      unit_id.value = null;
      if (val) loadUnits(val);
      else units.value = [];
    };

    const onStoreSelect = async (storeId) => {
      storeProducts.value = [];
      items.value = [EMPTY_ITEM()];
      if (!storeId) return;
      const res = await axios.get(`/admin/store/product-in-store/${storeId}`);
      const products = res?.data?.data?.data ?? [];
      storeProducts.value = products.map(p => ({
        value: p.id,
        name: p.bar_code ? `${p.product_name} (${p.bar_code})` : p.product_name,
      }));
    };

    const onProductSelect = async (productId, idx) => {
      if (!productId) return;
      const res = await axios.get(`/inventory/product/${productId}?store_id=${store_id.value}`);
      const p   = res?.data?.data;
      if (!p) return;
      items.value[idx].variants           = p.variants ?? [];
      items.value[idx].uom                = p.unit_of_measurement ?? '';
      items.value[idx].available_qty      = p.store_qty ?? 0;
      items.value[idx].product_variant_id = '';
    };

    const addItem    = () => items.value.push(EMPTY_ITEM());
    const removeItem = (idx) => { if (items.value.length > 1) items.value.splice(idx, 1); };

    const submit = async () => {
      validationErrors.value = [];

      if (!store_id.value)        { validationErrors.value = ['Please select a store.']; return; }
      if (!requisition_for.value) { validationErrors.value = ['Please select who this requisition is for.']; return; }
      if (!purpose.value.trim())  { validationErrors.value = ['Please enter a purpose.']; return; }

      if (requisition_for.value === 'department' && !department_id.value) {
        validationErrors.value = ['Please select a department.']; return;
      }
      if (requisition_for.value === 'individual' && !individual_details.value.trim()) {
        validationErrors.value = ['Please enter the individual details.']; return;
      }

      const validItems = items.value.filter(i => i.product_id);
      if (!validItems.length) { validationErrors.value = ['Please add at least one product.']; return; }

      const variantErr = validItems.find(i => i.variants.length > 0 && !i.product_variant_id);
      if (variantErr) { validationErrors.value = ['Please select a variant for all variant products.']; return; }

      // Build the department text sent to the backend
      let departmentValue = null;
      if (requisition_for.value === 'department') {
        const dept = departments.value.find(d => d.value === department_id.value);
        departmentValue = dept?.name ?? null;
      } else {
        departmentValue = individual_details.value.trim() || null;
      }

      const selectedUnit = units.value.find(u => u.value === unit_id.value);

      crudLoading.value = true;
      try {
        const res = await axios.post('/inventory/internal-issue', {
          store_id:        store_id.value,
          requisition_for: requisition_for.value,
          department:      departmentValue,
          unit:            selectedUnit?.name ?? null,
          purpose:         purpose.value,
          notes:           notes.value,
          date:            date.value instanceof Date ? date.value.toISOString().split('T')[0] : date.value,
          time:            new Date().toTimeString().slice(0, 8),
          items: validItems.map(i => ({
            product_id:         i.product_id,
            product_variant_id: i.product_variant_id || null,
            quantity:           parseInt(i.quantity),
            notes:              i.notes || null,
          })),
        });
        toast.success(res.data.message);
        router.push('/all-internal-issues');
      } catch (err) {
        const msg = err?.response?.data?.message;
        validationErrors.value = Array.isArray(msg) ? msg : (msg ? [String(msg)] : ['Something went wrong.']);
      } finally {
        crudLoading.value = false;
      }
    };

    onMounted(() => {
      allStore();
      loadDepartments();
    });

    return {
      date, store_id, requisition_for, department_id, unit_id, individual_details,
      purpose, notes, items, storeProducts, departments, units,
      getStore, crudLoading, validationErrors,
      onStoreSelect, onProductSelect, onRequisitionTypeChange, onDepartmentSelect,
      addItem, removeItem, submit,
    };
  },
};
</script>

<style src="@vueform/multiselect/themes/default.css"></style>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.error-banner { display:flex; align-items:flex-start; gap:10px; background:#fff5f5; border:1px solid #fac5c5; border-radius:10px; padding:12px 16px; margin-bottom:20px; color:#e03535; font-size:13px; }
.error-icon   { font-size:16px; margin-top:2px; flex-shrink:0; }

.form-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:24px; }
.mb-4 { margin-bottom:20px; }

.section-header { display:flex; align-items:center; gap:14px; margin-bottom:18px; }
.section-icon { width:42px; height:42px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:17px; flex-shrink:0; }
.section-icon--blue { background:rgba(0,74,173,.1); color:#004AAD; }
.section-icon--teal { background:rgba(13,148,136,.1); color:#0d9488; }
.section-title { font-size:14px; font-weight:700; color:#1a2b45; margin:0 0 2px; }
.section-sub   { font-size:12px; color:#7a90b0; margin:0; }

.fields-grid         { display:grid; gap:16px; }
.fields-grid--2      { grid-template-columns:repeat(2,1fr); }
.field-group         { display:flex; flex-direction:column; gap:6px; }
.field-group--full   { grid-column:1/-1; }
.field-label         { font-size:13px; font-weight:600; color:#1a2b45; }
.req                 { color:#e03535; }
.field-input         { border:1px solid #dde5f0; border-radius:8px; padding:9px 12px; font-size:13px; color:#1a2b45; background:#fff; outline:none; resize:vertical; transition:border-color .15s; }
.field-input:focus   { border-color:#004AAD; }
.field-select        { appearance:auto; cursor:pointer; }

.datepicker-wrap { position:relative; }
.datepicker-icon { position:absolute; left:11px; top:50%; transform:translateY(-50%); color:#7a90b0; font-size:13px; z-index:2; pointer-events:none; }
:deep(.clean-datepicker) { border:1px solid #dde5f0 !important; border-radius:8px !important; background:#fff !important; padding-left:32px !important; }
:deep(.clean-datepicker input) { font-size:13.5px; color:#1a2b45; }
:deep(.clean-datepicker.is-active) { border-color:#004AAD !important; box-shadow:0 0 0 3px rgba(0,74,173,.08) !important; }

:deep(.custom-multiselect) {
  --ms-border-color:#dde5f0; --ms-border-radius:8px;
  --ms-font-size:13.5px; --ms-tag-bg:#edf4ff; --ms-tag-color:#004AAD;
  --ms-ring-color:rgba(0,74,173,.08);
}
:deep(.custom-multiselect.is-active) { --ms-border-color:#004AAD; }
:deep(.custom-multiselect--sm) { --ms-py:6px; --ms-font-size:13px; }

.table-outer-scroll { overflow-x:auto; }
.table-scroll-wrap { overflow:visible; min-width:700px; }
.req-table { width:100%; border-collapse:collapse; }
.req-table thead tr { background:#f8fafc; }
.req-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:10px 8px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.req-table tbody tr { border-bottom:1px solid #f0f4f8; }
.req-table tbody tr:last-child { border-bottom:none; }
.req-table tbody tr:hover { background:#fafbfc; }
.req-table tbody td { padding:8px; vertical-align:middle; }

.tbl-input { width:100%; padding:7px 9px; border:1px solid #dde5f0; border-radius:7px; font-size:13px; color:#1a2b45; background:#fff; outline:none; transition:border-color .15s; }
.tbl-input:focus    { border-color:#004AAD; box-shadow:0 0 0 2px rgba(0,74,173,.08); }
.tbl-input:disabled { background:#f8fafc; color:#aab8cc; }
.tbl-input::placeholder { color:#aab8cc; }
.tbl-select { appearance:auto; cursor:pointer; }
.tbl-input--center { text-align:center; }

.btn-remove-row { width:30px; height:30px; border-radius:7px; background:#fff0f0; border:1px solid #fac5c5; color:#e03535; display:flex; align-items:center; justify-content:center; font-size:12px; cursor:pointer; transition:background .15s; }
.btn-remove-row:hover { background:#ffe0e0; }

.table-footer-actions { margin-top:24px; padding-top:16px; border-top:1px solid #f0f4f8; }
.btn-add-row { display:inline-flex; align-items:center; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; font-size:13px; font-weight:600; padding:8px 18px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-add-row:hover { background:#d6e4fa; }

.form-footer { display:flex; align-items:center; justify-content:flex-end; gap:10px; padding-top:4px; }
.btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 20px; border-radius:8px; cursor:pointer; transition:background .15s; }
.btn-outline:hover { background:#f4f7fb; }
.btn-primary-action { display:inline-flex; align-items:center; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 24px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }

.me-2 { margin-right:6px; }
@media(max-width:768px) { .fields-grid--2 { grid-template-columns:1fr; } .form-card { padding:16px; } .form-footer { flex-direction:column; } .btn-primary-action,.btn-outline { width:100%; justify-content:center; } }
</style>
